import { AlertCircle, Heart, ShieldAlert, ShieldX } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PaymentMethodDeclineState } from '@/types/contact';

/**
 * Wave 2 §6.4 / §7.3 — payment-method health chip.
 *
 *   HEALTHY        → emerald, "Healthy"
 *   SOFT_DECLINED  → amber,   "Soft decline"
 *   HARD_DECLINED  → red,     "Hard decline"
 *   INVALID        → orange,  "Invalid"
 *
 * The chip is intentionally compact so it can sit next to the
 * `<CreditCard …·•••• 4242>` row in `ContactPaymentMethodsSection` without
 * pushing the layout. We only render anything when the state is
 * non-HEALTHY — a HEALTHY chip everywhere would be visual noise.
 */
export interface PaymentMethodHealthBadgeProps {
  state?: PaymentMethodDeclineState | null;
  className?: string;
}

const META: Record<
  PaymentMethodDeclineState,
  {
    label: string;
    classes: string;
    Icon: typeof Heart;
  }
> = {
  HEALTHY: {
    label: 'Healthy',
    classes: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Icon: Heart,
  },
  SOFT_DECLINED: {
    label: 'Soft decline',
    classes: 'bg-amber-50 text-amber-700 border border-amber-200',
    Icon: AlertCircle,
  },
  HARD_DECLINED: {
    label: 'Hard decline',
    classes: 'bg-red-50 text-red-700 border border-red-200',
    Icon: ShieldX,
  },
  INVALID: {
    label: 'Invalid',
    classes: 'bg-orange-50 text-orange-700 border border-orange-200',
    Icon: ShieldAlert,
  },
};

export function PaymentMethodHealthBadge({ state, className }: PaymentMethodHealthBadgeProps) {
  const resolved = state ?? 'HEALTHY';
  // Skip rendering for HEALTHY — quieter UI.
  if (resolved === 'HEALTHY') return null;
  const { label, classes, Icon } = META[resolved];
  return (
    <span
      data-payment-method-health={resolved}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium leading-none',
        classes,
        className,
      )}
      title={`Payment method state: ${label}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
